import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit3, Trash2, Play, Calendar, Clock, Headphones, Download } from 'lucide-react';
import type { PodcastWithRelations } from '../../hooks/usePodcasts';
import { Pagination } from '../shared/Pagination';
import { scrollToSection } from '../../utils/scroll';

interface PodcastsSectionProps {
  podcasts: PodcastWithRelations[];
  isLoading: boolean;
  error: any;
  isAdminMode: boolean;
  onCreatePodcast: () => void;
  onEditPodcast: (podcast: PodcastWithRelations) => void;
  onDeletePodcast: (id: string) => void;
  onPlayPodcast: (podcast: PodcastWithRelations) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
}

export const PodcastsSection: React.FC<PodcastsSectionProps> = ({
  podcasts,
  isLoading,
  error,
  isAdminMode,
  onCreatePodcast,
  onEditPodcast,
  onDeletePodcast,
  onPlayPodcast,
  currentPage,
  onPageChange
}) => {
  const podcastsPerPage = 6;
  const totalPages = Math.ceil(podcasts.length / podcastsPerPage);
  const startIndex = (currentPage - 1) * podcastsPerPage;
  const endIndex = startIndex + podcastsPerPage;
  const currentPodcasts = podcasts.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    onPageChange(page);
    scrollToSection('podcasts-section');
  };

  return (
    <div id="podcasts-section">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-2xl font-bold">Gestão de Podcasts</h2>
        <button
          onClick={onCreatePodcast}
          className="hidden md:flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Adicionar Podcast
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent"></div>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          Erro ao carregar podcasts. Por favor, tente novamente.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isAdminMode && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card rounded-lg p-6 border-2 border-dashed border-primary/30 hover:border-primary/50 transition-colors cursor-pointer flex flex-col items-center justify-center gap-4 text-center"
              onClick={onCreatePodcast}
            >
              <Plus className="w-12 h-12 text-primary" />
              <p className="text-lg font-semibold">Adicionar Novo Podcast</p>
              <p className="text-sm text-gray-400">Clique para criar</p>
            </motion.div>
          )}
          {(!podcasts || podcasts.length === 0) ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-400">Nenhum podcast encontrado.</p>
            </div>
          ) : (
            podcasts.map((podcast, index) => (
              <motion.div
                key={podcast.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -12, transition: { duration: 0.2 } }}
                className="relative bg-card/20 backdrop-blur-[2px] rounded-2xl p-6 shadow-xl border border-white/5 overflow-hidden group h-full flex flex-col hover:shadow-2xl hover:shadow-primary/20 hover:border-primary/50 transition-all duration-300"
              >
                <div className="relative h-40 shrink-0 mb-4 rounded-xl overflow-hidden">
                  <img
                    src={podcast.image_url || '/placeholder-podcast.jpg'}
                    alt={podcast.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>

                <div className="flex flex-col flex-grow">
                  <div className="flex justify-between items-start">
                    <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
                      {podcast.category?.name || 'Sem categoria'}
                    </span>
                  </div>

                  <h3 className="text-xl font-semibold mt-3 mb-2">{podcast.title}</h3>
                  <p className="text-sm text-gray-400 mb-4 line-clamp-2">{podcast.description}</p>

                  <div className="flex items-center justify-between text-sm text-gray-400 mb-3">
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {new Date(podcast.published_at || '').toLocaleDateString('pt-PT')}
                    </span>
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {podcast.duration}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-400 mb-3">
                    <span className="flex items-center gap-2">
                      <Headphones className="w-4 h-4" />
                      {podcast.plays} reproduções
                    </span>
                    <span className="flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      {podcast.downloads || 0} downloads
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {podcast.tags?.map((tag) => (
                      <span
                        key={tag.id}
                        className="px-2 py-1 bg-secondary/50 rounded-full text-xs"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={() => onPlayPodcast(podcast)}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      Ouvir
                    </button>
                  </div>
                </div>

                {isAdminMode && (
                  <div className="mt-auto pt-4">
                    <div className="h-px bg-white/10 mb-4" />
                    <div className="flex items-center justify-center gap-4">
                      <button
                        onClick={() => onEditPodcast(podcast)}
                        className="flex items-center gap-2 px-6 py-2.5 bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
                      >
                        <Edit3 className="w-4 h-4" />
                        Editar
                      </button>
                      <button
                        onClick={() => onDeletePodcast(podcast.id)}
                        className="flex items-center gap-2 px-6 py-2.5 bg-destructive/10 hover:bg-destructive/20 text-destructive rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Apagar
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>
      )}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        onClick={onCreatePodcast}
        className="md:hidden fixed right-4 bottom-20 z-50 flex flex-col items-center px-3 py-2 bg-primary text-primary-foreground rounded-lg shadow-lg hover:bg-primary/90 transition-colors text-xs font-medium w-[100px]"
      >
        <Plus className="w-3.5 h-3.5 mb-0.5" />
        <span className="text-center leading-tight">Adicionar Podcast</span>
      </motion.button>
    </div>
  );
};